import moment from 'moment'
import { DateTime } from 'luxon'
import { getUserTimezone as getTimezone } from './timezoneStorage'

// Re-export getUserTimezone so other files can import from dateUtils
export { getTimezone as getUserTimezone }

// Internal helper to get user timezone
const getUserTimezone = getTimezone

/**
 * Convert moment/Date/string to ISO string for API
 * Always returns UTC ISO string
 * @param {moment.Moment|Date|string} input - Date to convert
 * @returns {string|null} ISO string or null
 */
export const toISOString = (input) => {
  if (!input) return null

  let date
  if (moment.isMoment(input)) {
    date = input.toDate()
  } else if (typeof input === 'string') {
    date = new Date(input)
  } else {
    date = input
  }

  return date.toISOString()
}

/**
 * Create date range for API queries using Luxon
 * Returns ISO strings for start and end of day in USER'S SELECTED timezone
 * @param {moment.Moment|Date|string} date - Date to create range for
 * @returns {{start: string, end: string}} ISO strings for start and end of day
 */
export const createDateRange = (date) => {
  const userTimezone = getUserTimezone()

  let jsDate
  if (moment.isMoment(date)) {
    jsDate = date.toDate()
  } else if (typeof date === 'string') {
    jsDate = new Date(date)
  } else {
    jsDate = date
  }

  // Create DateTime in user's timezone
  const dt = DateTime.fromJSDate(jsDate, { zone: userTimezone })

  // Get start and end of day in user's timezone
  const startOfDay = dt.startOf('day')
  const endOfDay = dt.endOf('day')

  return {
    start: startOfDay.toISO(),
    end: endOfDay.toISO(),
  }
}

/**
 * Parse API response date to moment
 * API returns ISO strings (UTC)
 * @param {string} isoString - ISO date string from API
 * @returns {moment.Moment|null} Moment object or null
 */
export const parseAPIDate = (isoString) => {
  if (!isoString) return null
  return moment(isoString) // Moment parses ISO as UTC, converts to local
}

/**
 * Format date for display in user's timezone
 * @param {moment.Moment|Date|string} date - Date to format
 * @param {string} format - Moment format string (default: 'DD/MM/YYYY')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return ''

  // Keep using moment for formatting since it's already used throughout the app
  return moment(date).format(format)
}

/**
 * Format datetime for display in user's timezone
 * @param {moment.Moment|Date|string} date - Date to format
 * @param {string} format - Moment format string (default: 'DD/MM/YYYY HH:mm')
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date, format = 'DD/MM/YYYY HH:mm') => {
  if (!date) return ''

  // Keep using moment for formatting since it's already used throughout the app
  return moment(date).format(format)
}

/**
 * Validate ISO date string
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid ISO date string
 */
export const isValidISODate = (dateString) => {
  if (!dateString) return false
  const dt = DateTime.fromISO(dateString)
  return dt.isValid
}

/**
 * Get current date/time in user's timezone
 * @returns {moment.Moment} Current moment in user's timezone
 */
export const now = () => {
  return moment()
}

/**
 * Create operation time boundary range
 * Returns a time range centered around "now" with the given boundary in hours
 * Used for paxCount queries that need to include recent check-ins
 *
 * @param {number} boundaryHours - Hours before and after now (default: 12)
 * @returns {{start: string, end: string}} Object with start and end ISO strings
 */
export const createOperationTimeBoundary = (boundaryHours = 12) => {
  const userTimezone = getUserTimezone()

  // Get current time in user's timezone
  const nowDt = DateTime.now().setZone(userTimezone)

  // Calculate boundaries
  const startTime = nowDt.minus({ hours: boundaryHours })
  const endTime = nowDt.plus({ hours: boundaryHours })

  return {
    start: startTime.toISO(),
    end: endTime.toISO(),
  }
}

/**
 * Create daily reset boundary (3 AM to 3 AM)
 * Similar to backend's getDailyTimeBoundary function
 * Used for summary reports and booking summaries
 *
 * @param {moment.Moment|Date|string} date - Date to create boundary for (optional, defaults to now)
 * @param {Object} resetTime - Reset time config (default: {hour: 3, minute: 0})
 * @returns {{start: string, end: string}} Object with start and end ISO strings
 */
export const createDailyResetBoundary = (
  date = null,
  resetTime = { hour: 3, minute: 0 },
) => {
  const userTimezone = getUserTimezone()

  // Convert input to DateTime in user's timezone
  let currentDt
  if (!date) {
    currentDt = DateTime.now().setZone(userTimezone)
  } else if (moment.isMoment(date)) {
    currentDt = DateTime.fromJSDate(date.toDate(), { zone: userTimezone })
  } else if (typeof date === 'string') {
    currentDt = DateTime.fromJSDate(new Date(date), { zone: userTimezone })
  } else {
    currentDt = DateTime.fromJSDate(date, { zone: userTimezone })
  }

  // Get the reset time (3 AM) for the current day
  const dailyResetDateTime = currentDt.set({
    hour: resetTime.hour,
    minute: resetTime.minute,
    second: 0,
    millisecond: 0,
  })

  let start, end

  // If current time is before the reset time (e.g., 2 AM),
  // the boundary is: yesterday 3 AM to today 3 AM
  // Otherwise, the boundary is: today 3 AM to tomorrow 3 AM
  if (currentDt <= dailyResetDateTime) {
    start = dailyResetDateTime.minus({ days: 1 })
    end = dailyResetDateTime
  } else {
    start = dailyResetDateTime
    end = dailyResetDateTime.plus({ days: 1 })
  }

  return {
    start: start.toISO(),
    end: end.toISO(),
  }
}
